<template>
  <v-flex
    v-if="family !== null"
    xs12
    class="mt-5"
  >
    <div class="mb-3">
      <h2 class="display-3">
        {{ $t('preview.family_needs.title') }}
      </h2>
      <router-link
        :disabled="readOnly"
        :class="{ disabled: readOnly }"
        :to="{ name: 'sheet-two', params: { familyId: $route.params.familyId } }"
      >
        {{ $t('buttons.edit_needs') }}
      </router-link>
    </div>
    <FamilyNeedsItem
      v-if="family.formattedNeeds.food.chosenProducts.length > 0"
      :title="$t('sheet.sheet_two.food.title')"
      :items="getFullList(sections.food, lists.food, others.food)"
    >
      <template slot="notes">
        <div v-if="family.formattedNeeds.food.hasDietaryRequirements === 'yes'">
          <p class="fw-600 mb-1 mt-4">
            {{ $t('preview.family_needs.special_requirements') }}
          </p>
          <p class="mb-0 formatted-text">
            {{ family.formattedNeeds.food.dietaryRequirements }}
          </p>
        </div>
      </template>
    </FamilyNeedsItem>
    <FamilyNeedsItem
      v-if="family.formattedNeeds.cleaningProducts.chosenProducts.length > 0"
      :title="$t('sheet.sheet_two.cleaning_products.title')"
      :items="
        getFullList(sections.cleaningProducts, lists.cleaningProducts, others.cleaningProducts)
      "
    >
      <template slot="notes">
        <div v-if="family.formattedNeeds.cleaningProducts.hasSpecialRequirements === 'yes'">
          <p class="fw-600 mb-1 mt-4">
            {{ $t('preview.family_needs.special_requirements') }}
          </p>
          <p class="mb-0 formatted-text">
            {{ family.formattedNeeds.cleaningProducts.specialRequirements }}
          </p>
        </div>
      </template>
    </FamilyNeedsItem>
    <FamilyNeedsItem
      v-if="family.formattedNeeds.clothing.needClothes === 'yes'"
      :title="$t('preview.family_needs.clothes')"
      :items="family.formattedNeeds.clothing.chosenClothesDetails"
      is-clothes
    />
    <FamilyNeedsItem
      v-if="family.formattedNeeds.clothing.needFootwear === 'yes'"
      :title="$t('preview.family_needs.footwear')"
      :items="family.formattedNeeds.clothing.chosenFootwearDetails"
      is-clothes
    />
    <FamilyNeedsItem
      v-if="family.formattedNeeds.schoolSupplies.needSchoolSupplies === 'yes'"
      :title="$t('sheet.sheet_two.school_supplies.title')"
      :items="getFullList(sections.schoolSupplies, lists.schoolSupplies, others.schoolSupplies)"
    />
    <FamilyNeedsItem
      v-if="family.formattedNeeds.apartmentEquipment.selectedEquipment.length > 0"
      :title="$t('sheet.sheet_two.apartment_equipment.title')"
      :items="
        getFullList(
          sections.apartmentEquipment,
          lists.apartmentEquipment,
          others.apartmentEquipment,
          true
        )
      "
    >
      <template slot="notes">
        <div v-if="family.formattedNeeds.apartmentEquipment.notes !== null">
          <p class="fw-600 mb-1 mt-4">
            {{ $t('preview.family_needs.notes') | addColons }}
          </p>
          <p class="mb-0 formatted-text">
            {{ family.formattedNeeds.apartmentEquipment.notes }}
          </p>
        </div>
      </template>
    </FamilyNeedsItem>
    <FamilyNeedsItem
      v-if="family.formattedNeeds.householdAppliances.selectedAppliances.length > 0"
      :title="$t('preview.family_needs.household_title')"
      :items="
        getFullList(
          sections.householdAppliances,
          lists.householdAppliances,
          others.householdAppliances
        )
      "
    >
      <template slot="notes">
        <div v-if="family.formattedNeeds.householdAppliances.notes !== null">
          <p class="fw-600 mb-1 mt-4">
            {{ $t('preview.family_needs.notes') | addColons }}
          </p>
          <p class="mb-0 formatted-text">
            {{ family.formattedNeeds.householdAppliances.notes }}
          </p>
        </div>
      </template>
    </FamilyNeedsItem>
    <FamilyNeedsItem
      v-if="
        family.formattedNeeds.householdAppliances.hasOwnProperty(
          'vitalElectronicAppliancesNeeds'
        ) && family.formattedNeeds.householdAppliances.vitalElectronicAppliancesNeeds === 'yes'
      "
      :title="$t('preview.family_needs.vital_electronic_appliances')"
      :items="family.formattedNeeds.householdAppliances.vitalElectronicAppliances"
    >
      <template slot="notes">
        <div
          v-if="
            family.formattedNeeds.householdAppliances.hasOwnProperty(
              'vitalElectronicAppliancesNotes'
            ) && family.formattedNeeds.householdAppliances.vitalElectronicAppliancesNotes !== null
          "
        >
          <p class="fw-600 mb-1 mt-4">
            {{ $t('preview.family_needs.notes') | addColons }}
          </p>
          <p class="mb-0 formatted-text">
            {{ family.formattedNeeds.householdAppliances.vitalElectronicAppliancesNotes }}
          </p>
        </div>
      </template>
    </FamilyNeedsItem>
    <FamilyNeedsItem
      v-if="family.formattedNeeds.otherMaterialNeeds.hasOtherNeeds === 'yes'"
      :title="$t('sheet.sheet_two.other_material_needs.title')"
      :items="
        getFullList(
          sections.otherMaterialNeeds,
          lists.otherMaterialNeeds,
          others.otherMaterialNeeds
        )
      "
    >
      <template slot="fuelInfo">
        <div v-if="family.formattedNeeds.otherMaterialNeeds.fuelType">
          <span>{{ fuelTypeTitle }}</span>
          <p class="mb-0 f-05">
            {{ $t('sheet.sheet_two.other_material_needs.labels.amount') | addColons }}
            {{ family.formattedNeeds.otherMaterialNeeds.fuelAmount }}
          </p>
        </div>
      </template>
      <template slot="notes">
        <div
          v-if="
            family.formattedNeeds.otherMaterialNeeds.hasOwnProperty('anotherMaterialNeedsNotes') &&
              family.formattedNeeds.otherMaterialNeeds.anotherMaterialNeedsNotes !== null
          "
        >
          <p class="fw-600 mb-1 mt-4">
            {{ $t('preview.family_needs.notes') | addColons }}
          </p>
          <p class="mb-0 formatted-text">
            {{ family.formattedNeeds.otherMaterialNeeds.anotherMaterialNeedsNotes }}
          </p>
        </div>
      </template>
    </FamilyNeedsItem>
    <FamilyNeedsItem
      v-if="family.formattedNeeds.intangibleNeeds.hasIntangibleNeeds === 'yes'"
      :title="$t('sheet.sheet_two.intangible_needs.title')"
      :items="family.formattedNeeds.intangibleNeeds.intangibleNeedsList"
      :show-divider="false"
    />
  </v-flex>
</template>

<script>
import FamilyNeedsItem from '@/components/widgets/previewForDonor/FamilyNeedsItem';

import filters from '@/constants/filters';
import has from 'lodash.has';

export default {
    name: 'FamilyNeeds',
    components: { FamilyNeedsItem },
    filters: {
        ...filters
    },
    props: {
        readOnly: {
            type: Boolean,
            required: true
        }
    },
    data: () => ({
        exceptions: {
            other: 'Inne',
            childrenProducts: 'Artykuły dziecięce',
            whatLiquidCleaningProducts: 'Płyny czyszczące',
            productsForBabies: 'Produkty dla niemowląt',
            otherFuel: 'Inny'
        },
        sections: {
            food: 'food',
            cleaningProducts: 'cleaningProducts',
            schoolSupplies: 'schoolSupplies',
            apartmentEquipment: 'apartmentEquipment',
            householdAppliances: 'householdAppliances',
            otherMaterialNeeds: 'otherMaterialNeeds'
        },
        lists: {
            food: 'chosenProducts',
            cleaningProducts: 'chosenProducts',
            schoolSupplies: 'selectedSupplies',
            apartmentEquipment: 'selectedEquipment',
            householdAppliances: 'selectedAppliances',
            otherMaterialNeeds: 'selectedMaterialNeeds'
        },
        others: {
            food: 'otherProducts',
            cleaningProducts: 'otherProducts',
            schoolSupplies: 'anotherSchoolSupplies',
            apartmentEquipment: 'anotherEquipment',
            householdAppliances: 'anotherAppliances',
            otherMaterialNeeds: 'anotherMaterialNeeds',
            childrenProducts: 'childrenProducts',
            whatLiquidCleaningProducts: 'whatLiquidCleaningProducts',
            productsForBabies: 'productsForBabies'
        }
    }),
    computed: {
        family () {
            return this.$store.getters['previewForDonor/getPreviewInfo'];
        },
        fuelTypeTitle () {
            if (this.family.formattedNeeds.otherMaterialNeeds.fuelType === this.exceptions.otherFuel) {
                return this.family.formattedNeeds.otherMaterialNeeds.otherFuelType;
            }
            return this.family.formattedNeeds.otherMaterialNeeds.fuelType;
        },
        isCurrentEdition () {
            return this.$store.getters['family/isCurrentEdition'];
        }
    },
    methods: {
        getFullList (section, list, other) {
            if (has(this.family.formattedNeeds[section], other)) {
                let _arr;
                if (Array.isArray(this.family.formattedNeeds[section][other])) {
                    _arr = [
                        ...this.family.formattedNeeds[section][list],
                        ...this.family.formattedNeeds[section][other]
                    ];
                } else {
                    _arr = [
                        ...this.family.formattedNeeds[section][list],
                        this.family.formattedNeeds[section][other]
                    ];
                }
                const _other = _arr.indexOf(this.exceptions.other);
                if (_other !== -1) {
                    _arr.splice(_other, 1);
                }
                if (section === this.sections.cleaningProducts) {
                    const _child = _arr.indexOf(this.exceptions.childrenProducts);
                    if (_child !== -1) {
                        _arr.splice(_child, 1);
                        _arr.push(this.family.formattedNeeds[section].childrenProducts);
                    }
                    const _child2 = _arr.indexOf(this.exceptions.whatLiquidCleaningProducts);
                    if (_child2 !== -1) {
                        _arr.splice(_child2, 1);
                        _arr.push(this.family.formattedNeeds[section].whatLiquidCleaningProducts);
                    }
                }
                if (section === this.sections.food) {
                    const _child = _arr.indexOf(this.exceptions.productsForBabies);
                    if (_child !== -1) {
                        _arr.splice(_child, 1);
                        _arr.push(this.family.formattedNeeds[section].productsForBabies);
                    }
                }
                return _arr;
            } else if (section === this.sections.cleaningProducts) {
                let _arr = [...this.family.formattedNeeds[section][list]];
                if (has(this.family.formattedNeeds[section], this.others.childrenProducts)) {
                    _arr = [..._arr, this.family.formattedNeeds[section].childrenProducts];
                    const _child = _arr.indexOf(this.exceptions.childrenProducts);
                    if (_child !== -1) {
                        _arr.splice(_child, 1);
                    }
                }
                if (has(this.family.formattedNeeds[section], this.others.whatLiquidCleaningProducts)) {
                    _arr = [..._arr, this.family.formattedNeeds[section].whatLiquidCleaningProducts];
                    const _child = _arr.indexOf(this.exceptions.whatLiquidCleaningProducts);
                    if (_child !== -1) {
                        _arr.splice(_child, 1);
                    }
                }
                return _arr;
            } else if (
                section === this.sections.food &&
        has(this.family.formattedNeeds[section], this.others.productsForBabies)
            ) {
                const _arr = [
                    ...this.family.formattedNeeds[section][list],
                    this.family.formattedNeeds[section].productsForBabies
                ];
                const _child = _arr.indexOf(this.exceptions.productsForBabies);
                if (_child !== -1) {
                    _arr.splice(_child, 1);
                }
                return _arr;
            } else {
                return this.family.formattedNeeds[section][list];
            }
        }
    }
};
</script>

<style scoped></style>
